import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';
import superjson from 'superjson';

export interface TaskingBotProject {
  id: string;
  userId: number;
  name: string;
  description: string | null;
  categoryId: string;
  parentProjectId: string | null;
  color: string | null;
  icon: string | null;
  position: number;
  taskCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

const QUERY_KEY = ['taskingbot', 'projects'];

export function useTaskingBotProjects(categoryId?: string) {
  return useQuery({
    queryKey: [...QUERY_KEY, categoryId],
    queryFn: async (): Promise<TaskingBotProject[]> => {
      const params = new URLSearchParams();
      if (categoryId) params.set('categoryId', categoryId);
      const qs = params.toString();
      const res = await fetch(`/_api/taskingbot/projects${qs ? `?${qs}` : ''}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch projects');
      const data = superjson.parse<{ projects: TaskingBotProject[] }>(await res.text());
      return data.projects;
    },
    staleTime: 10000,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      categoryId: string;
      parentProjectId?: string | null;
      color?: string;
      icon?: string;
    }) => {
      const res = await fetch('/_api/taskingbot/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Failed to create project';
        try {
          msg = superjson.parse<{ error: string }>(text).error || msg;
        } catch {
          // Keep default message when response is not superjson.
        }
        throw new Error(msg);
      }
      return superjson.parse<{ project: TaskingBotProject }>(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      name?: string;
      description?: string;
      categoryId?: string;
      parentProjectId?: string | null;
      color?: string;
      icon?: string;
      position?: number;
    }) => {
      const res = await fetch('/_api/taskingbot/projects/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Failed to update project';
        try { msg = superjson.parse<{ error: string }>(text).error || msg; } catch { try { msg = JSON.parse(text).error || msg; } catch {} }
        throw new Error(msg);
      }
      return superjson.parse<{ project: TaskingBotProject }>(await res.text());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => {
      const res = await fetch('/_api/taskingbot/projects/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify({ projectId }),
      });
      if (!res.ok) throw new Error('Failed to delete project');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useReorderProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectIds: string[]; categoryId: string }) => {
      const res = await fetch('/_api/taskingbot/projects/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: superjson.stringify(input),
      });
      if (!res.ok) throw new Error('Failed to reorder projects');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
